import React, { useState, useEffect, useCallback } from 'react';
import { AddApplication2 } from 'containers/Family/AddApplication2/AddApplication2';
import { ADD_APPLICATION_VIEW_MAP } from 'containers/Family/AddApplication2/AddApplication2';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom';
import {
  Breadcrumb,
  Card,
  Tabs,
  Typography,
  Row,
  Col,
  Spin,
  Button,
  Popconfirm,
  Tooltip,
  Dropdown,
  Space,
  message,
} from 'antd';
import { ArrowLeft, Chemistry, ChemistryReference, ChevronDown } from '@carbon/icons-react';
import { useParams } from 'react-router-dom/cjs/react-router-dom';
import { selectRoles } from 'features/auth';
import { useSelector } from 'react-redux';
import { ROLES } from 'constants';
import { familyService } from 'services';
import { formatDate, getFullName, hasPermission } from 'utils';
import queryString from 'query-string';
import { FamilyDocuments } from './FamilyDocuments';
import { ENDASH } from 'constants';

const { Paragraph } = Typography;

export default function FamilyApplication() {
  const roles = useSelector(selectRoles);
  const history = useHistory();
  const location = useLocation();
  const { householdId, id: applicationId } = useParams();
  const searchParams = queryString.parse(location.search);

  const [loading, setLoading] = useState(false);
  const [household, setHousehold] = useState(null);
  const [application, setApplication] = useState(null);
  const [popConfirmIsOpen, setPopConfirmIsOpen] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      familyService.getHouseHold(householdId),
      applicationId ? familyService.getApplication(applicationId) : Promise.resolve(null),
    ])
      .then(([householdData, applicationData]) => {
        setHousehold(householdData);
        setApplication(applicationData);
      })
      .catch((error) => {
        console.error('Error fetching data', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [householdId, applicationId]);

  const _isTestAccount = household?.testRecord === true;
  const _userHasPermissionToToggleAsTest = hasPermission(roles, [ROLES.FAMILY_MANAGER]);

  const handleToggleTestAccount = () => {
    if (!_userHasPermissionToToggleAsTest) {
      return;
    }

    const _newTestRecord = !_isTestAccount;

    familyService
      .updateHousehold(householdId, {
        testRecord: _newTestRecord,
      })
      .then(() => {
        message.success('Done');
        fetchData();
      })
      .catch((error) => {
        console.error('Error updating household', error);
        message.error('Something went wrong');
      });
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // Set default tab if not present
    if (!searchParams.activeTab) {
      searchParams.activeTab = 'application-details';
      history.replace(`${location.pathname}?${queryString.stringify(searchParams)}`);
    }
  }, [history, location.pathname, searchParams]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-[1024px] mx-auto mb-8 py-8 px-4 md:py-0 md:px-0">
        <Breadcrumb
          items={[
            {
              href: '',
              title: (
                <div className="flex gap-3 items-center">
                  <ArrowLeft className="!w-4 !h-4" />
                  <span>Back to Family Queue</span>
                </div>
              ),
              className: '!flex !items-center !gap-2 !text-[#334155]',
              onClick: () => {
                history.push('/families');
              },
            },
          ]}
        />

        {/* Header Section */}
        <Card
          title={
            <>
              {getFullName(application?.applicant) || ENDASH} - F#{householdId}
            </>
          }
          extra={
            <Popconfirm
              placement="bottomRight"
              description={
                <div className="w-[70vw] max-w-[250px]">
                  {!_isTestAccount ? (
                    <>
                      You are about to change this to a test account. This will{' '}
                      <strong className="font-semibold">remove</strong> it from processing and reporting.
                      <p className="block mt-4">Are you sure you'd like to proceed?</p>
                    </>
                  ) : (
                    <>
                      You are about to convert this record to a real account. This will{' '}
                      <strong className="font-semibold">include</strong> it in processing and processing and reporting.
                      <p className="block mt-4">Are you sure you'd like to proceed?</p>
                    </>
                  )}
                </div>
              }
              onConfirm={handleToggleTestAccount}
              onCancel={null}
              okText={!_isTestAccount ? 'Mark as Test' : 'Mark as Real Account'}
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              open={_isTestAccount ? popConfirmIsOpen : undefined}
              onOpenChange={(open) => {
                if (_isTestAccount && open === false) {
                  setPopConfirmIsOpen(open);
                }
              }}
            >
              <Tooltip
                title={!_userHasPermissionToToggleAsTest ? 'Only Family Managers can toggle the test status' : ''}
              >
                {!_isTestAccount ? (
                  <Button disabled={!_userHasPermissionToToggleAsTest} icon={<ChemistryReference />}>
                    Mark as Test Account
                  </Button>
                ) : _userHasPermissionToToggleAsTest ? (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          label: 'Mark as real account',
                          key: 'mark-as-real',
                          onClick: () => {
                            setPopConfirmIsOpen(true);
                          },
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button icon={<Chemistry className="mb-[-1px] ml-[-1px] mr-[-2px]" />}>
                      <Space size={3}>
                        Test Account
                        {_userHasPermissionToToggleAsTest && <ChevronDown className="mb-[-1px]" />}
                      </Space>
                    </Button>
                  </Dropdown>
                ) : (
                  <Button disabled>Test Account</Button>
                )}
              </Tooltip>
            </Popconfirm>
          }
          className="mt-6 mb-6"
        >
          <Row gutter={[24, 8]}>
            <Col span={8}>
              <Paragraph className="!mb-1">
                <strong>Application ID:</strong> {application?.id || ENDASH}
              </Paragraph>

              <Paragraph className="!mb-1">
                <strong>Submitted:</strong> {application?.submittedDate ? formatDate(application.submittedDate) : 'N/A'}
              </Paragraph>

              <Paragraph className="!mb-1">
                <strong>Program:</strong> {application?.programType?.title || ENDASH}
              </Paragraph>
            </Col>

            <Col span={8}>
              <Paragraph className="!mb-1">
                <strong>Branch:</strong> {application?.applicant?.militaryDetails?.branch?.title || ENDASH}
              </Paragraph>

              <Paragraph className="!mb-1">
                <strong>Login User:</strong> {application?.createdBy?.fullName || 'N/A'}
                <br />
                {application?.createdBy?.email && (
                  <Typography.Link href={`mailto:${application.createdBy.email}`}>
                    {application.createdBy.email}
                  </Typography.Link>
                )}
              </Paragraph>
            </Col>
          </Row>
        </Card>

        {/* Tabs Section */}
        <Tabs
          activeKey={searchParams.activeTab}
          items={[
            {
              key: 'application-details',
              label: 'Application details',
              children: (
                <div>
                  <AddApplication2
                    applicationView={ADD_APPLICATION_VIEW_MAP.REVIEW}
                    baseRoute={`/families/${householdId}/applications`}
                    readOnly={!roles.includes(ROLES.SYSTEM_ADMIN)}
                  />
                </div>
              ),
            },
            {
              key: 'documents',
              label: 'Documents',
              children: <FamilyDocuments application={application} household={household} />,
            },
          ]}
          onChange={(key) => {
            searchParams.activeTab = key;
            history.replace(`${location.pathname}?${queryString.stringify(searchParams)}`);
          }}
          className="mt-12"
        />
      </div>
    </div>
  );
}
